import React, { useState, createRef, RefObject } from "react";
import { Modal, styled, Text, Heading, TextLevel } from "@sourceful/shared-components";
import { SanityLearnMoreModal } from "@sourceful/shared-types";
import toRem from "../../styles/postprocess/conversion/to-rem";
import LearnMoreModalCTA from "./LearnMoreModalCTA";
import LearnMoreModalContentBlock from "./LearnMoreModalContentBlock";
import { useIsSsr } from "@lib/hooks/useIsSsr";

const Wrapper = styled("div", {
  maxWidth: "650px",
});

const TitleWrapper = styled("div", {
  textAlign: "center",
  marginBottom: toRem(20),
});

const Grid = styled("div", {
  display: "grid",
  gridRowGap: toRem(10),
  maxHeight: "70vh",
});

const Button = styled("button", {
  all: "unset",
  color: "#747477",
  cursor: "pointer",
  textDecoration: "underline",
  border: "1px solid transparent",
  width: "fit-content",
  borderRadius: "$small",
  "&:hover": {
    color: "$palette1_colour1",
  },
  "&:focus": {
    color: "$palette1_colour1",
    border: "1px solid $palette1_colour1",
  },
  // backwards compatible focus visible https://css-tricks.com/platform-news-using-focus-visible-bbcs-new-typeface-declarative-shadow-doms-a11y-and-placeholders/
  "&:focus:not(:focus-visible)": {
    border: "1px solid transparent",
  },
});

interface LearnMoreModalProps {
  learnMoreModal: SanityLearnMoreModal;
  isOpenByDefault?: boolean;
  renderTrigger?: (ref: RefObject<HTMLButtonElement>, handleOpen: () => void) => JSX.Element;
  triggerFontLevel?: TextLevel;
}

export const LearnMoreModal = ({
  renderTrigger,
  learnMoreModal,
  isOpenByDefault = false,
  triggerFontLevel = 3,
}: LearnMoreModalProps) => {
  const isSSR = useIsSsr();
  const { triggerLabel, imagePosition, textAlign, modal } = learnMoreModal;

  const { title, subtitle, contentBlocks } = modal;

  const [isModalOpen, setIsModalOpen] = useState(isOpenByDefault);
  const buttonRef = createRef<HTMLButtonElement>();

  if (isSSR) {
    return null;
  }

  const portalRoot = window.document.getElementById("portal-root");

  const handleOpen = () => setIsModalOpen(true);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(buttonRef, handleOpen)
      ) : (
        <Button
          type="button"
          ref={buttonRef}
          onClick={handleOpen}
          className="learn-more-modal-trigger"
        >
          <Text level={triggerFontLevel}>{triggerLabel}</Text>
        </Button>
      )}

      <Modal
        id={title}
        triggerRef={buttonRef}
        isOpen={isModalOpen}
        handleClose={() => {
          setIsModalOpen(false);
        }}
        portalRoot={portalRoot || document.body}
        padding="40px"
      >
        <Wrapper>
          <TitleWrapper>
            <Heading level={4}>{title}</Heading>
            <Heading level={6} prominence="primary">
              {subtitle}
            </Heading>
          </TitleWrapper>

          <Grid>
            {contentBlocks?.map(block => {
              if (block._type === "configuratorModalCTABlock") {
                return <LearnMoreModalCTA key={block._key} cta={block} />;
              }

              return (
                <LearnMoreModalContentBlock
                  key={block._key}
                  block={block}
                  textAlign={textAlign}
                  imagePosition={imagePosition}
                />
              );
            })}
          </Grid>
        </Wrapper>
      </Modal>
    </>
  );
};

export default LearnMoreModal;
